import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PageBackdrop } from "@/components/PageBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const InsightsPage = () => {
  usePageMeta("Vagus Digital | Insights for small businesses");
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    fetch(`${API}/posts`)
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-paper pb-24 pt-32 lg:pt-40">
      <PageBackdrop src="/art/insights.webp" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="text-xs font-medium uppercase tracking-[0.3em] text-jade"
        >
          Insights
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl"
          data-testid="insights-heading"
        >
          Short, useful guides. <em className="italic text-jade">No filler</em>.
        </motion.h1>
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.14 }}
          className="mt-5 max-w-xl leading-relaxed text-ink2"
        >
          Practical notes on websites, AI tools, SEO and social. Written for
          small business owners, not for search engines.
        </motion.p>

        {posts === null && (
          <p className="mt-16 text-ink2" data-testid="insights-loading">Loading insights…</p>
        )}

        {Array.isArray(posts) && posts.length === 0 && (
          <div className="mt-16 rounded-2xl bg-white p-10 shadow-ambient" data-testid="insights-empty">
            <p className="leading-relaxed text-ink2">
              The first guides are being written. In the meantime, the answers
              to most questions live on the About page, or ask the assistant,
              bottom right.
            </p>
          </div>
        )}

        {Array.isArray(posts) && posts.length > 0 && (
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3" data-testid="insights-grid">
            {posts.map((post, i) => (
              <motion.article
                key={post.slug}
                {...fadeUp}
                transition={{ duration: 0.7, delay: 0.08 * i }}
                className="group"
                data-testid={`insight-card-${post.slug}`}
              >
                <Link to={`/insights/${post.slug}`} data-testid={`insight-link-${post.slug}`} className="block">
                  <div className="aspect-[3/2] overflow-hidden rounded-2xl shadow-ambient">
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <span className="rounded-full border border-jade/20 px-3 py-1 text-xs text-jade">
                      {post.tag}
                    </span>
                    <span className="text-xs text-ink2/60">{post.reading_time} min read</span>
                  </div>
                  <h2 className="mt-3 flex items-start justify-between gap-2 font-display text-2xl font-medium leading-snug text-ink transition-colors duration-300 group-hover:text-jade">
                    {post.title}
                    <ArrowUpRight className="mt-1.5 h-4 w-4 shrink-0 text-jade/60 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-ink2/80">{post.summary}</p>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InsightsPage;